// Pure, framework-free logic for coach work-time tracking. Kept separate from
// the provider so it can be unit-tested without React/DOM/Apollo (the repo's
// unit tests run in a node environment).

export const HEARTBEAT_INTERVAL_MS = 25 * 1000
// The status dot shows "counting" (green) only while the server is actually
// crediting time. If no heartbeat has raised the week total within this window
// (a bit over one beat, to tolerate a single deduped beat), we treat the coach
// as not accruing — e.g. they're in a live call — and grey the dot.
export const CREDIT_STALENESS_MS = HEARTBEAT_INTERVAL_MS + 15 * 1000
// A coach is "active" if they took a real action (scroll/click/type) in the
// last 60s while the tab is visible. Quiet reading inside this window counts.
export const IDLE_AFTER_MS = 60 * 1000
// Coalesce the firehose of interaction events to at most one touch per second.
export const INTERACTION_THROTTLE_MS = 1000
export const TICK_MS = 1000
// Genuine work actions only — scrolling, clicking, typing. Passive mouse
// movement and merely having the tab open do NOT count as active.
export const ACTIVITY_EVENTS = ['scroll', 'wheel', 'click', 'keydown']
// Warn the coach once their tracked time reaches this fraction of the weekly
// budget (budget = 20% of last week's call time, computed server-side).
export const WARN_FRACTION = 0.8

/** Whether the coach is actively working right now. */
export const isActive = ({
  visible,
  lastInteractionAt,
  now,
  idleAfterMs = IDLE_AFTER_MS,
}: {
  visible: boolean
  lastInteractionAt: number
  now: number
  idleAfterMs?: number
}): boolean => visible && now - lastInteractionAt < idleAfterMs

/**
 * Advance the dot's "credit tracking" state when a fresh server week-total
 * arrives. The dot means "the server is crediting time right now" — only a
 * heartbeat *ack* that raises the total (`fromCredit`) proves that. A
 * seed/refetch can also raise the total without any live crediting (e.g. the
 * coach returns to a tab during a live call), so it must update the displayed
 * total without advancing `creditedAt`. `seenSeconds` is the high-water mark,
 * so a later deduped / in-call ack returning the same total can't re-green the
 * dot.
 */
export const nextCreditState = ({
  prevSeenSeconds,
  prevCreditedAt,
  seconds,
  fromCredit,
  now,
}: {
  prevSeenSeconds: number
  prevCreditedAt: number
  seconds: number
  fromCredit: boolean
  now: number
}): { seenSeconds: number; creditedAt: number } => ({
  seenSeconds: seconds,
  creditedAt: fromCredit && seconds > prevSeenSeconds ? now : prevCreditedAt,
})

/**
 * Whether the server credited time recently enough to show the dot as
 * "counting". With `creditedAt` initialised to 0 (no credit yet), this is
 * false until the first credited heartbeat — so the dot never flashes green on
 * mount, and stays grey while a coach is in a live call (beats are dropped
 * server-side, so the total stops rising).
 */
export const isCrediting = ({
  creditedAt,
  now,
  stalenessMs = CREDIT_STALENESS_MS,
}: {
  creditedAt: number
  now: number
  stalenessMs?: number
}): boolean => creditedAt > 0 && now - creditedAt < stalenessMs

/**
 * Whether the coach has reached the warning point — `warnFraction` of their
 * weekly budget. No warning when there's no budget (no calls last week).
 */
export const isOverWarnThreshold = ({
  displaySeconds,
  weeklyBudgetSeconds,
  warnFraction = WARN_FRACTION,
}: {
  displaySeconds: number
  weeklyBudgetSeconds: number
  warnFraction?: number
}): boolean =>
  weeklyBudgetSeconds > 0 &&
  displaySeconds >= warnFraction * weeklyBudgetSeconds

/**
 * Format tracked seconds as "Xh Ym". The readout is server-driven and updates
 * per heartbeat (~25s), so we show minute granularity rather than ticking
 * seconds (which previously drifted from the server total and jumped backward).
 */
export const formatTrackedDuration = (totalSeconds: number): string => {
  const total = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  return `${hours}h ${minutes}m`
}
